import { useState } from 'react';
import { darken, alpha, styled } from '@mui/system';
import { MainNavbar } from '../componentsv2/navigation/MainNavbar';
import { MainSidebar } from '../components/main/main-sidebar';
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useKeycloak } from '@react-keycloak/ssr';
import { utils } from '../utils';
import { useUser } from '../contexts/UserContext';
import { useOrganizationData } from '../contexts/OrganizationContext';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { getOrganizationStatusMessage, hasOrganizationLatePayment, isOrganizationAccessDisabled } from '../utils/organizationStatus';
import { useOrganizationMembership } from '@/session/OrganizationEntryGuard';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { useSessionIdentityGeneration } from '@/session/useSessionIdentityGeneration';

const MainLayoutRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  paddingTop: 64,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 280,
  },
}));

interface MainLayoutProps {
  children?: React.ReactNode;
}

export const MainLayout = (props: MainLayoutProps) => {
  const { children } = props;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useUser();
  const { keycloak, initialized } = useKeycloak();
  const { organization } = useOrganizationData();
  const { format: formatZonedDate } = useTimezoneFormatter();
  const { state: membershipState, retrying: membershipRetrying, retry: retryMembership } = useOrganizationMembership();

  const currentOrganization = useCommittedOrganization();
  // isAcendaStaff reads the committed session snapshot during render; subscribe so the staff
  // bypass below is not decided on a pre-commit render that sees no realm roles.
  useSessionIdentityGeneration();
  const isPrivilegedUser = utils.authUtils.isAcendaStaff();
  const isOrganizationReady = !currentOrganization || organization !== null;
  const accessDisabled = organization ? isOrganizationAccessDisabled(organization) : false;
  const accessDisabledMessage = organization ? getOrganizationStatusMessage(organization, formatZonedDate) : '';
  const hasLatePayment = organization ? hasOrganizationLatePayment(organization) : false;

  return initialized && keycloak?.authenticated ? (
    <>
      <MainLayoutRoot id='main-layout-root'>
        <Box
          id='firstbox'
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            width: '100%',
            paddingBottom: '4.5rem',
          }}
        >
          {membershipState === 'error' && (
            <Alert
              severity='warning'
              sx={{ borderRadius: 0 }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  aria-disabled={membershipRetrying}
                  onClick={() => {
                    // aria-disabled (not disabled) keeps focus on the button
                    // through the retry, matching the session error surfaces.
                    if (!membershipRetrying) retryMembership();
                  }}
                >
                  {membershipRetrying ? 'Retrying…' : 'Retry'}
                </Button>
              }
            >
              We could not confirm your access to this organization. Showing previously loaded data.
            </Alert>
          )}
          {!isOrganizationReady ? null : accessDisabled && !isPrivilegedUser ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 6 }}>
              <Card sx={{ maxWidth: 640, width: '100%' }}>
                <CardContent>
                  <Typography variant='h6' gutterBottom>
                    Access Disabled
                  </Typography>
                  <Alert severity='error'>{accessDisabledMessage || 'Access to this organization is disabled.'}</Alert>
                  {hasLatePayment ? (
                    <Alert severity='error' sx={{ mt: 2 }}>
                      Please reach out to accounting@acenda.com to resolve issues with your account.
                    </Alert>
                  ) : null}
                </CardContent>
              </Card>
            </Box>
          ) : (
            children
          )}
          {/* <pre>          {JSON.stringify(theme.palette, null, 2)}
          </pre> */}
        </Box>
      </MainLayoutRoot>
      <MainNavbar onOpenSidebar={() => setIsSidebarOpen(true)} user={user} key={`navbar-${currentOrganization}`} />
      <MainSidebar onClose={() => setIsSidebarOpen(false)} open={isSidebarOpen} user={user} key={`sidebar-${currentOrganization}`} />
    </>
  ) : (
    <></>
  );
};
